import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { 
  Building, Building2, Briefcase, MapPin, Plus, Edit, Users, Settings, LogOut, 
  BarChart3, Eye, CheckCircle, XCircle, Clock, TrendingUp, FileText, Upload, 
  Trash2, Download, Calendar, Video, Phone, Award, UserCheck, Bell, ChevronDown,
  Search, Filter, ArrowUpRight, ArrowDownRight, Loader2, Mail, Globe, Star,
  UserPlus, ClipboardList, CalendarDays, Target, Zap, Send, MoreVertical,
  BadgeCheck, GraduationCap, IndianRupee, ExternalLink, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOB_TYPES = ['Full Time', 'Part Time', 'Contract', 'Internship', 'Freelance'];

// =============== Indian States ===============
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Chandigarh', 'Jammu & Kashmir', 'Ladakh', 'Puducherry',
  'Andaman & Nicobar', 'Dadra & Nagar Haveli', 'Daman & Diu', 'Lakshadweep'
];

const INDUSTRY_TYPES = [
  'IT / Software', 'Manufacturing', 'Construction', 'Automobile', 'Healthcare',
  'Education', 'Retail', 'BFSI', 'Hospitality', 'Textiles', 'Agriculture',
  'Logistics', 'Telecom', 'Energy', 'FMCG', 'Real Estate', 'Other'
];

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+', '5000+'];

const ORG_TYPES = ['Private', 'Government', 'NGO', 'School', 'College', 'University', 'Public Sector', 'Other'];

const BRANCH_TYPES = ['Head Office', 'Branch Office', 'Plant', 'Factory', 'Warehouse', 'Regional Office'];

// =============== Section Card Wrapper ===============
const SectionCard = ({ icon: Icon, title, subtitle, accent = 'blue', children }) => {
  const gradients = {
    blue: 'from-blue-500 to-indigo-600',
    emerald: 'from-emerald-500 to-teal-600',
    violet: 'from-violet-500 to-purple-600',
    amber: 'from-amber-500 to-orange-600',
    rose: 'from-rose-500 to-pink-600',
    slate: 'from-slate-600 to-slate-700',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradients[accent] || gradients.blue} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-slate-900 text-base">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
};

// =============== Field Component ===============
const FormField = ({ label, required, error, hint, children }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
    {children}
    {error ? (
      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
        <XCircle className="w-3 h-3" />{error}
      </p>
    ) : hint ? (
      <p className="text-[11px] text-slate-400 mt-1">{hint}</p>
    ) : null}
  </div>
);

// =============== Company Profile Editor ===============
const CompanyProfileEditor = React.memo(({ profile, setProfile, user, API }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const logoInputRef = useRef(null);
  const [form, setForm] = useState({
    company_name: '', industry_type: '', company_size: '', website: '', logo_url: '',
    email: '', phone: '', alternate_phone: '',
    address: '', city: '', district: '', state: '', pin_code: '',
    gst_number: '', interested_naps_nats: '', organization_type: '', year_established: '',
    about: '', branches: []
  });
  const [newBranch, setNewBranch] = useState({ name: '', type: 'Branch Office', address: '', city: '', district: '', state: '', pin_code: '', contact_person: '', contact_phone: '' });

  React.useEffect(() => {
    if (!editing) {
      setForm({
        company_name: profile?.company_name || '',
        industry_type: profile?.industry_type || profile?.industry || '',
        company_size: profile?.company_size || '',
        website: profile?.website || '',
        logo_url: profile?.logo_url || '',
        email: profile?.email || user?.email || '',
        phone: profile?.phone || '',
        alternate_phone: profile?.alternate_phone || '',
        address: profile?.address || profile?.company_address || '',
        city: profile?.city || '',
        district: profile?.district || '',
        state: profile?.state || '',
        pin_code: profile?.pin_code || '',
        gst_number: profile?.gst_number || '',
        interested_naps_nats: profile?.interested_naps_nats || '',
        organization_type: profile?.organization_type || '',
        year_established: profile?.year_established || '',
        about: profile?.about || '',
        branches: profile?.branches || []
      });
      setErrors({});
    }
  }, [profile, user, editing]);

  // ============ Validation ============
  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = 'Company name is required';
    if (!form.industry_type) e.industry_type = 'Please select industry';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email format';
    if (!form.phone.trim()) e.phone = 'Phone is required';
    else if (!/^[+\d][\d\s-]{7,15}$/.test(form.phone)) e.phone = 'Invalid phone (10 digits expected)';
    if (form.alternate_phone && !/^[+\d][\d\s-]{7,15}$/.test(form.alternate_phone)) e.alternate_phone = 'Invalid phone';
    if (!form.address.trim()) e.address = 'Head office address is required';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.state) e.state = 'State is required';
    if (!form.pin_code.trim()) e.pin_code = 'Pin code is required';
    else if (!/^\d{6}$/.test(form.pin_code)) e.pin_code = 'Pin code must be 6 digits';
    if (form.year_established && !/^(19|20)\d{2}$/.test(form.year_established)) e.year_established = 'Enter valid year (e.g. 2010)';
    if (form.website && !/^https?:\/\/.+/.test(form.website)) e.website = 'URL should start with http:// or https://';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ============ Logo Upload ============
  const processLogoFile = (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPG / PNG files are allowed');
      return;
    }
    if (file.size > 100 * 1024) {
      toast.error('Logo size must be under 100 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, logo_url: ev.target.result }));
    reader.readAsDataURL(file);
    toast.success('Logo loaded - Save to apply');
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    processLogoFile(file);
  };

  // ============ Save ============
  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted errors');
      // Scroll to first error
      setTimeout(() => {
        const firstErr = document.querySelector('[data-field-error="true"]');
        firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, is_complete: true };
      await axios.put(`${API}/employer/profile`, payload);
      setProfile(prev => ({ ...prev, ...payload }));
      setEditing(false);
      toast.success('Profile updated successfully', {
        description: 'Aap ab job posting kar sakte hain'
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const addBranch = () => {
    if (!newBranch.name || !newBranch.city) { toast.error('Branch name aur city required hai'); return; }
    const branch = { ...newBranch, id: Date.now().toString(), created_at: new Date().toISOString() };
    setForm(prev => ({ ...prev, branches: [...prev.branches, branch] }));
    setNewBranch({ name: '', type: 'Branch Office', address: '', city: '', district: '', state: '', pin_code: '', contact_person: '', contact_phone: '' });
    toast.success('Branch added - Save profile to apply');
  };

  const removeBranch = (idx) => {
    setForm(prev => ({ ...prev, branches: prev.branches.filter((_, i) => i !== idx) }));
  };

  // ============ Read-only View ============
  if (!editing) {
    const completionPct = (() => {
      const fields = ['company_name', 'industry_type', 'company_size', 'email', 'phone', 'address', 'city', 'state', 'pin_code', 'about'];
      const filled = fields.filter(f => (form[f] || '').toString().trim()).length;
      return Math.round((filled / fields.length) * 100);
    })();
    const isComplete = profile?.is_complete && completionPct >= 80;

    return (
      <div className="space-y-5">
        {/* Top hero card with logo + completion */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex flex-col md:flex-row items-start gap-4">
            <div className="w-20 h-20 rounded-2xl border-2 border-slate-100 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="w-full h-full object-contain" />
              ) : (
                <Building2 className="w-10 h-10 text-blue-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-bold text-slate-900">{form.company_name || 'Company Name'}</h2>
                {isComplete ? (
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-emerald-200">
                    <BadgeCheck className="w-3 h-3" /> Verified Profile
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-amber-200">
                    <Clock className="w-3 h-3" /> Incomplete
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-1">{form.industry_type || '—'} • {form.company_size ? `${form.company_size} employees` : 'Size not specified'}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                {form.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{form.email}</span>}
                {form.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{form.phone}</span>}
                {form.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{form.website}</span>}
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Profile Completeness</span>
                  <span className="text-[11px] font-bold text-slate-700">{completionPct}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${completionPct >= 80 ? 'bg-emerald-500' : completionPct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${completionPct}%` }} />
                </div>
              </div>
            </div>
            <Button onClick={() => setEditing(true)} className="bg-blue-600 hover:bg-blue-700 rounded-lg" data-testid="edit-profile-btn">
              <Edit className="w-4 h-4 mr-1.5" /> Edit Profile
            </Button>
          </div>
        </div>

        {/* Detail Sections - read only */}
        <SectionCard icon={Building} title="Company Details" accent="blue">
          <div className="grid md:grid-cols-2 gap-4">
            {[
              ['Industry', form.industry_type],
              ['Company Size', form.company_size],
              ['Website', form.website],
              ['GST Number', form.gst_number],
              ['Type of Organization', form.organization_type],
              ['Established', form.year_established],
            ].map(([l, v]) => (
              <div key={l} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                <span className="text-xs text-slate-500 font-medium">{l}</span>
                <span className="text-sm text-slate-800 font-semibold">{v || '—'}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard icon={MapPin} title="Address" accent="emerald">
          <p className="text-sm text-slate-700 leading-relaxed">
            {form.address || '—'}{form.address && (form.city || form.state) ? ', ' : ''}
            {[form.city, form.district, form.state, form.pin_code].filter(Boolean).join(', ')}
          </p>
        </SectionCard>

        {form.about && (
          <SectionCard icon={FileText} title="About Company" accent="violet">
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{form.about}</p>
          </SectionCard>
        )}

        {/* Branches */}
        <SectionCard
          icon={Building2}
          title={`Branches / Plants / Factories (${form.branches.length})`}
          accent="amber"
        >
          {form.branches.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Building2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No branches added yet</p>
              <Button onClick={() => setEditing(true)} variant="outline" size="sm" className="mt-3 rounded-lg">
                <Plus className="w-4 h-4 mr-1" /> Add Branches
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {form.branches.map((b, i) => (
                <div key={b.id || i} className="border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all" data-testid={`branch-${i}`}>
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-500" /> {b.name}
                    </h4>
                    <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold uppercase">{b.type}</span>
                  </div>
                  <p className="text-xs text-slate-600 flex items-start gap-1">
                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {[b.address, b.city, b.district, b.state].filter(Boolean).join(', ')}{b.pin_code ? ` - ${b.pin_code}` : ''}
                  </p>
                  {b.contact_person && (
                    <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                      <UserCheck className="w-3 h-3" /> {b.contact_person} {b.contact_phone ? `• ${b.contact_phone}` : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    );
  }

  // ============ Edit Mode ============
  const ActionBar = ({ position = 'top' }) => (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-3 ${position === 'top' ? 'mb-1' : 'mt-2'} ${position === 'bottom' ? 'sticky bottom-0 bg-slate-50/80 backdrop-blur p-4 -mx-1 z-10 rounded-2xl border border-slate-200 shadow-lg' : ''}`}>
      {position === 'top' && (
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-900">Edit Company Profile</h2>
          <p className="text-sm text-slate-500 mt-0.5">Update karein taaki aap jobs post kar sakein</p>
        </div>
      )}
      {position === 'bottom' && Object.keys(errors).length > 0 && (
        <div className="flex-1 text-sm text-red-600 flex items-center gap-1.5 font-medium">
          <XCircle className="w-4 h-4" /> Please fix {Object.keys(errors).length} error{Object.keys(errors).length > 1 ? 's' : ''} above
        </div>
      )}
      <Button variant="outline" onClick={() => setEditing(false)} className="rounded-lg sm:order-1 order-2 border-slate-300" data-testid={`cancel-profile-${position}`}>
        Cancel
      </Button>
      <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 rounded-lg sm:order-2 order-1 px-6 shadow-md shadow-blue-100" data-testid={`save-profile-${position}`}>
        {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Save Profile</>}
      </Button>
    </div>
  );

  return (
    <div className="space-y-5">
      <ActionBar position="top" />

      {/* SECTION 1: Basic Details */}
      <SectionCard icon={Building} title="Company Basic Details" subtitle="Core information about your business" accent="blue">
        {/* Logo Upload */}
        <div className="mb-6">
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Company Logo</Label>
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-2xl border-2 border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="w-full h-full object-contain" />
              ) : (
                <Building2 className="w-10 h-10 text-slate-300" />
              )}
            </div>
            <div
              onClick={() => logoInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleLogoDrop}
              className={`flex-1 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50/30'}`}
              data-testid="logo-upload-zone"
            >
              <Upload className="w-6 h-6 text-blue-500 mx-auto mb-1.5" />
              <p className="text-sm font-semibold text-slate-700">Drop logo here or click to upload</p>
              <p className="text-[11px] text-slate-400 mt-0.5">JPG / PNG • Max 100 KB</p>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={(e) => processLogoFile(e.target.files?.[0])}
                className="hidden"
                data-testid="logo-input"
              />
              {form.logo_url && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setForm(f => ({ ...f, logo_url: '' })); }}
                  className="text-[11px] text-red-500 hover:underline mt-2 inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Remove logo
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <FormField label="Company Name" required error={errors.company_name}>
            <Input
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              placeholder="e.g., Iqra Tech Pvt Ltd"
              className={`rounded-lg h-10 ${errors.company_name ? 'border-red-300' : ''}`}
              data-field-error={!!errors.company_name}
              data-testid="profile-company-name"
            />
          </FormField>
          <FormField label="Industry Type" required error={errors.industry_type}>
            <select
              value={form.industry_type}
              onChange={(e) => setForm({ ...form, industry_type: e.target.value })}
              className={`w-full h-10 px-3 border rounded-lg bg-white text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none ${errors.industry_type ? 'border-red-300' : 'border-slate-200'}`}
              data-field-error={!!errors.industry_type}
              data-testid="profile-industry"
            >
              <option value="">Select Industry</option>
              {INDUSTRY_TYPES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </FormField>
          <FormField label="Company Size">
            <select
              value={form.company_size}
              onChange={(e) => setForm({ ...form, company_size: e.target.value })}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg bg-white text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
            >
              <option value="">Select Size</option>
              {COMPANY_SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
            </select>
          </FormField>
          <FormField label="Website URL" error={errors.website} hint="Include https://">
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://yourcompany.com"
                className={`rounded-lg h-10 pl-10 ${errors.website ? 'border-red-300' : ''}`}
                data-field-error={!!errors.website}
              />
            </div>
          </FormField>
          <FormField label="GST Number">
            <Input
              value={form.gst_number}
              onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
              className="rounded-lg h-10"
            />
          </FormField>
        </div>
      </SectionCard>

      {/* SECTION 2: Contact Information */}
      <SectionCard icon={Phone} title="Contact Information" subtitle="How candidates and Iqra Skills can reach you" accent="emerald">
        <div className="grid md:grid-cols-2 gap-4">
          <FormField label="Official Email" required error={errors.email}>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="hr@company.com"
                className={`rounded-lg h-10 pl-10 ${errors.email ? 'border-red-300' : ''}`}
                data-field-error={!!errors.email}
                data-testid="profile-email"
              />
            </div>
          </FormField>
          <FormField label="Phone Number" required error={errors.phone}>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91XXXXXXXXXX"
                className={`rounded-lg h-10 pl-10 ${errors.phone ? 'border-red-300' : ''}`}
                data-field-error={!!errors.phone}
                data-testid="profile-phone"
              />
            </div>
          </FormField>
          <FormField label="Alternate Contact" error={errors.alternate_phone} hint="Optional secondary number">
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={form.alternate_phone}
                onChange={(e) => setForm({ ...form, alternate_phone: e.target.value })}
                placeholder="+91XXXXXXXXXX"
                className={`rounded-lg h-10 pl-10 ${errors.alternate_phone ? 'border-red-300' : ''}`}
                data-field-error={!!errors.alternate_phone}
              />
            </div>
          </FormField>
        </div>
      </SectionCard>

      {/* SECTION 3: Address Details */}
      <SectionCard icon={MapPin} title="Address Details" subtitle="Head office location" accent="violet">
        <div className="grid md:grid-cols-2 gap-4">
          <FormField label="Head Office Address" required error={errors.address} hint="Full street address">
            <textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Building name, street, area..."
              rows={2}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none resize-none ${errors.address ? 'border-red-300' : 'border-slate-200'}`}
              data-field-error={!!errors.address}
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="City" required error={errors.city}>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="City"
                className={`rounded-lg h-10 ${errors.city ? 'border-red-300' : ''}`}
                data-field-error={!!errors.city}
              />
            </FormField>
            <FormField label="District">
              <Input
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
                placeholder="District"
                className="rounded-lg h-10"
              />
            </FormField>
            <FormField label="State" required error={errors.state}>
              <select
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                className={`w-full h-10 px-3 border rounded-lg bg-white text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none ${errors.state ? 'border-red-300' : 'border-slate-200'}`}
                data-field-error={!!errors.state}
              >
                <option value="">Select State</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Pin Code" required error={errors.pin_code}>
              <Input
                value={form.pin_code}
                onChange={(e) => setForm({ ...form, pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="6-digit pin"
                maxLength={6}
                className={`rounded-lg h-10 ${errors.pin_code ? 'border-red-300' : ''}`}
                data-field-error={!!errors.pin_code}
              />
            </FormField>
          </div>
        </div>
      </SectionCard>

      {/* SECTION 4: Business Details */}
      <SectionCard icon={Briefcase} title="Business Details" subtitle="Help us understand your hiring needs" accent="amber">
        <div className="grid md:grid-cols-3 gap-4">
          <FormField label="Type of Organization">
            <select
              value={form.organization_type}
              onChange={(e) => setForm({ ...form, organization_type: e.target.value })}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg bg-white text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              data-testid="profile-org-type"
            >
              <option value="">Select Type</option>
              {ORG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </FormField>
          <FormField label="Year of Establishment" error={errors.year_established}>
            <Input
              type="number"
              value={form.year_established}
              onChange={(e) => setForm({ ...form, year_established: e.target.value })}
              placeholder="e.g., 2010"
              min="1900"
              max={new Date().getFullYear()}
              className={`rounded-lg h-10 ${errors.year_established ? 'border-red-300' : ''}`}
              data-field-error={!!errors.year_established}
            />
          </FormField>
          <FormField label="Interested in NAPS / NATS?">
            <select
              value={form.interested_naps_nats}
              onChange={(e) => setForm({ ...form, interested_naps_nats: e.target.value })}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg bg-white text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
            >
              <option value="">Select</option>
              <option value="NAPS">NAPS</option>
              <option value="NATS">NATS</option>
              <option value="Both">Both</option>
              <option value="No">Not Interested</option>
            </select>
          </FormField>
        </div>
      </SectionCard>

      {/* SECTION 5: About Company */}
      <SectionCard icon={FileText} title="Company Description" subtitle="Tell candidates about your company" accent="rose">
        <FormField label="About Company" hint={`${(form.about || '').length}/500`}>
          <textarea
            value={form.about}
            onChange={(e) => setForm({ ...form, about: e.target.value.slice(0, 500) })}
            placeholder="Briefly describe your company, services, and hiring focus..."
            rows={5}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none resize-y"
            data-testid="profile-about"
          />
        </FormField>
      </SectionCard>

      {/* SECTION 6: Branches */}
      <SectionCard icon={Building2} title={`Branches / Plants / Factories (${form.branches.length})`} subtitle="Add all your locations for accurate job postings" accent="slate">
        {form.branches.length > 0 && (
          <div className="space-y-2 mb-5">
            {form.branches.map((b, i) => (
              <div key={b.id || i} className="flex items-center gap-3 border border-slate-200 rounded-xl p-3 bg-slate-50/50 hover:bg-white transition-colors">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-800 text-sm truncate">{b.name}</p>
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold uppercase">{b.type}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {[b.city, b.district, b.state].filter(Boolean).join(', ')}{b.pin_code ? ` - ${b.pin_code}` : ''}
                  </p>
                  {b.contact_person && (
                    <p className="text-[11px] text-slate-400 truncate mt-0.5">Contact: {b.contact_person} {b.contact_phone ? `• ${b.contact_phone}` : ''}</p>
                  )}
                </div>
                <button onClick={() => removeBranch(i)} className="w-8 h-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center flex-shrink-0" data-testid={`remove-branch-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border-2 border-dashed border-blue-200 rounded-xl p-4 bg-blue-50/30">
          <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
            <Plus className="w-4 h-4 text-blue-600" /> Add New Branch
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <FormField label="Branch Name *">
              <Input value={newBranch.name} onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })} placeholder="Jaipur Plant" className="rounded-lg h-9 text-sm" data-testid="new-branch-name" />
            </FormField>
            <FormField label="Type">
              <select value={newBranch.type} onChange={(e) => setNewBranch({ ...newBranch, type: e.target.value })} className="w-full h-9 px-2 border border-slate-200 rounded-lg bg-white text-sm" data-testid="new-branch-type">
                {BRANCH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormField>
            <FormField label="City *">
              <Input value={newBranch.city} onChange={(e) => setNewBranch({ ...newBranch, city: e.target.value })} placeholder="City" className="rounded-lg h-9 text-sm" data-testid="new-branch-city" />
            </FormField>
            <FormField label="District">
              <Input value={newBranch.district} onChange={(e) => setNewBranch({ ...newBranch, district: e.target.value })} placeholder="District" className="rounded-lg h-9 text-sm" data-testid="new-branch-district" />
            </FormField>
            <FormField label="State">
              <select value={newBranch.state} onChange={(e) => setNewBranch({ ...newBranch, state: e.target.value })} className="w-full h-9 px-2 border border-slate-200 rounded-lg bg-white text-sm">
                <option value="">Select</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Pin Code">
              <Input value={newBranch.pin_code} onChange={(e) => setNewBranch({ ...newBranch, pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder="Pin" maxLength={6} className="rounded-lg h-9 text-sm" data-testid="new-branch-pincode" />
            </FormField>
            <FormField label="Address">
              <Input value={newBranch.address} onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })} placeholder="Full address" className="rounded-lg h-9 text-sm" />
            </FormField>
            <FormField label="Contact Person">
              <Input value={newBranch.contact_person} onChange={(e) => setNewBranch({ ...newBranch, contact_person: e.target.value })} placeholder="Name" className="rounded-lg h-9 text-sm" />
            </FormField>
            <FormField label="Contact Phone">
              <Input value={newBranch.contact_phone} onChange={(e) => setNewBranch({ ...newBranch, contact_phone: e.target.value })} placeholder="Phone" className="rounded-lg h-9 text-sm" />
            </FormField>
            <div className="flex items-end col-span-2 md:col-span-1">
              <Button type="button" onClick={addBranch} className="bg-blue-600 hover:bg-blue-700 rounded-lg w-full h-9 text-sm" data-testid="add-branch-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        </div>
      </SectionCard>

      <ActionBar position="bottom" />
    </div>
  );
});


const EmployerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const tabsSectionRef = useRef(null);
  const profileDropdownRef = useRef(null);
  
  // UI States
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // Close dropdown on route change
  useEffect(() => {
    setShowProfileMenu(false);
    setShowNotifications(false);
  }, [location.pathname]);

  // Click outside handler for profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowProfileMenu(false);
        setShowNotifications(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showProfileMenu]);

  // Function to change tab and scroll to it
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setTimeout(() => {
      tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  // Data States
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [enhancedAnalytics, setEnhancedAnalytics] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedJobApplications, setSelectedJobApplications] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [candidates, setCandidates] = useState([]);
  
  // Search & Filter States
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateFilters, setCandidateFilters] = useState({
    course: '',
    skills: '',
    location: '',
    certified: ''
  });
  
  // Job Form State
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobForm, setJobForm] = useState({
    position_name: '',
    location: '',
    num_requirements: '',
    min_ctc: '',
    max_ctc: '',
    job_type: 'Full Time',
    experience_min: 0,
    experience_max: 0,
    skills: '',
    description: '',
    requirements: '',
    spoc_name: '',
    spoc_mobile: '',
    spoc_email: '',
    application_deadline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Interview State
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [selectedApplicationForInterview, setSelectedApplicationForInterview] = useState(null);
  const [interviewForm, setInterviewForm] = useState({
    interview_date: '',
    interview_time: '',
    interview_type: 'In-Person',
    interview_location: '',
    interview_round: 1,
    interviewer_name: '',
    notes: ''
  });
  
  // Placement State
  const [placementDialogOpen, setPlacementDialogOpen] = useState(false);
  const [placementForm, setPlacementForm] = useState({
    joining_date: '',
    offered_ctc: '',
    designation: '',
    department: '',
    offer_letter_url: ''
  });

  // Real-time activity feed
  const [activityFeed, setActivityFeed] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Icon mapping for activity feed
  const activityIconMap = {
    FileText: FileText,
    Calendar: Calendar,
    BadgeCheck: BadgeCheck,
    UserPlus: UserPlus
  };

  // WebSocket connection for real-time updates
  const connectWebSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/employer?token=${token}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected for employer dashboard');
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'activity') {
          setActivityFeed(prev => {
            const newActivity = {
              id: `ws_${Date.now()}`,
              type: data.activity_type,
              message: data.message,
              time: data.time,
              icon: data.activity_type === 'job_application' ? 'FileText' : 'BadgeCheck',
              color: data.activity_type === 'job_application' ? 'blue' : 'emerald'
            };
            return [newActivity, ...prev.slice(0, 9)];
          });
          toast.success(data.message, { duration: 4000 });
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  };

  // Fetch real-time activity feed
  const fetchActivityFeed = async () => {
    try {
      const res = await axios.get(`${API}/activity/employer`);
      setActivityFeed(res.data || []);
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
      setActivityFeed([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Use activity feed for notifications (fallback to mock)
  const notifications = activityFeed.length > 0 ? activityFeed.slice(0, 3).map(a => ({
    id: a.id,
    type: a.type,
    message: a.message,
    time: a.time
  })) : [
    { id: 1, type: 'application', message: 'New application for Software Developer', time: '5 min ago' },
    { id: 2, type: 'interview', message: 'Interview scheduled with Rahul Kumar', time: '1 hr ago' },
    { id: 3, type: 'hired', message: 'Priya Sharma accepted offer!', time: '2 hrs ago' }
  ];

  useEffect(() => {
    fetchData();
    fetchActivityFeed();
    connectWebSocket();
    
    // Auto-refresh activity feed every 30 seconds (fallback)
    const activityInterval = setInterval(fetchActivityFeed, 30000);
    
    return () => {
      clearInterval(activityInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, analyticsRes, templatesRes, enhancedRes, placementsRes] = await Promise.all([
        axios.get(`${API}/employer/profile`),
        axios.get(`${API}/employer/analytics`),
        axios.get(`${API}/employer/job-templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/employer/enhanced-analytics`).catch(() => ({ data: null })),
        axios.get(`${API}/employer/placements`).catch(() => ({ data: [] }))
      ]);
      
      setProfile(profileRes.data);
      setAnalytics(analyticsRes.data);
      setTemplates(templatesRes.data);
      setEnhancedAnalytics(enhancedRes.data);
      setPlacements(placementsRes.data);
      
      if (analyticsRes.data?.jobs) {
        setJobs(analyticsRes.data.jobs);
      }

      // Fetch candidates
      try {
        const candidatesRes = await axios.get(`${API}/employer/candidates`);
        setCandidates(candidatesRes.data || []);
      } catch (e) {
        // Fetch from public job seekers
        try {
          const studentsRes = await axios.get(`${API}/students/job-seekers`);
          setCandidates(studentsRes.data || []);
        } catch (err) {}
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const handlePostJob = async (e) => {
    e.preventDefault();
    if (!jobForm.position_name || !jobForm.location) {
      toast.error('Please fill required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        ...jobForm,
        num_requirements: parseInt(jobForm.num_requirements) || 1,
        min_ctc: parseFloat(jobForm.min_ctc) || 0,
        max_ctc: parseFloat(jobForm.max_ctc) || 0,
        experience_min: parseInt(jobForm.experience_min) || 0,
        experience_max: parseInt(jobForm.experience_max) || 0
      };
      
      if (editingJob) {
        await axios.put(`${API}/employer/jobs/${editingJob.id}`, payload);
        toast.success('Job updated successfully!');
      } else {
        await axios.post(`${API}/employer/jobs`, payload);
        toast.success('Job posted successfully!');
      }
      
      setShowJobForm(false);
      setEditingJob(null);
      resetJobForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetJobForm = () => {
    setJobForm({
      position_name: '',
      location: '',
      num_requirements: '',
      min_ctc: '',
      max_ctc: '',
      job_type: 'Full Time',
      experience_min: 0,
      experience_max: 0,
      skills: '',
      description: '',
      requirements: '',
      spoc_name: profile?.spoc_name || '',
      spoc_mobile: profile?.spoc_mobile || '',
      spoc_email: profile?.spoc_email || '',
      application_deadline: ''
    });
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobForm({
      position_name: job.position_name || '',
      location: job.location || '',
      num_requirements: job.num_requirements?.toString() || '',
      min_ctc: job.min_ctc?.toString() || '',
      max_ctc: job.max_ctc?.toString() || '',
      job_type: job.job_type || 'Full Time',
      experience_min: job.experience_min || 0,
      experience_max: job.experience_max || 0,
      skills: job.skills || '',
      description: job.description || '',
      requirements: job.requirements || '',
      spoc_name: job.spoc_name || '',
      spoc_mobile: job.spoc_mobile || '',
      spoc_email: job.spoc_email || '',
      application_deadline: (job.application_deadline || '').slice(0, 10)
    });
    setShowJobForm(true);
  };

  // Block job posting if employer profile is incomplete
  const tryOpenJobForm = () => {
    if (!profile?.is_complete) {
      toast.error('Pehle Company Profile complete karein, fir job post kar sakte hain', {
        description: 'Profile tab par jaakar required details fill karein',
        duration: 4500
      });
      handleTabChange('profile');
      return;
    }
    resetJobForm();
    setEditingJob(null);
    setShowJobForm(true);
  };

  const handleCloseJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to close this job posting?')) return;
    try {
      await axios.put(`${API}/employer/jobs/${jobId}/status`, { status: 'closed' });
      toast.success('Job closed');
      fetchData();
    } catch (error) {
      toast.error('Failed to close job');
    }
  };

  const fetchApplications = async (jobId) => {
    try {
      const res = await axios.get(`${API}/employer/jobs/${jobId}/applications`);
      setSelectedJobApplications(res.data);
      setSelectedJob(jobs.find(j => j.id === jobId));
      setActiveTab('applications');
    } catch (error) {
      toast.error('Failed to fetch applications');
    }
  };


  // Download all candidates data as Excel
  const downloadCandidatesExcel = async () => {
    try {
      toast.info('Downloading candidates data...');
      const res = await axios.get(`${API}/employer/candidates`);
      const candidates = res.data || [];
      
      if (candidates.length === 0) {
        toast.error('No candidates to download');
        return;
      }
      
      // Prepare Excel data
      const excelData = candidates.map((c, idx) => ({
        'S.No': idx + 1,
        'Name': c.candidate_name || '',
        "Father's Name": c.candidate_father_name || '',
        'Date of Birth': c.candidate_dob || '',
        'Education': c.candidate_qualification || '',
        'Email': c.candidate_email || '',
        'Mobile Number': c.candidate_mobile || '',
        'City': c.candidate_city || '',
        'District': c.candidate_district || '',
        'State': c.candidate_state || '',
        'Pin Code': c.candidate_pin_code || '',
        'Applied For': c.job_title || '',
        'Application Status': c.status || 'pending',
        'Applied Date': c.applied_at ? new Date(c.applied_at).toLocaleDateString('en-IN') : '',
      }));
      
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 5 }, { wch: 22 }, { wch: 22 }, { wch: 14 }, { wch: 18 },
        { wch: 28 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 10 }, { wch: 22 }, { wch: 16 }, { wch: 14 }
      ];
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
      
      const fileName = `Candidates_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(`${candidates.length} candidates downloaded!`);
    } catch (e) {
      toast.error('Failed to download data');
    }
  };


  const handleApplicationStatus = async (applicationId, status) => {
    try {
      await axios.put(`${API}/employer/applications/${applicationId}/status`, { status });
      toast.success(`Application ${status}`);
      if (selectedJob) {
        fetchApplications(selectedJob.id);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleScheduleInterview = async (e) => {
    e.preventDefault();
    if (!interviewForm.interview_date || !interviewForm.interview_time) {
      toast.error('Please fill date and time');
      return;
    }
    
    try {
      await axios.post(`${API}/employer/interviews`, {
        application_id: selectedApplicationForInterview.id,
        ...interviewForm,
        scheduled_time: `${interviewForm.interview_date}T${interviewForm.interview_time}`
      });
      toast.success('Interview scheduled! Notification sent to candidate.');
      setInterviewDialogOpen(false);
      setSelectedApplicationForInterview(null);
      if (selectedJob) fetchApplications(selectedJob.id);
    } catch (error) {
      toast.error('Failed to schedule interview');
    }
  };

  const handleRecordPlacement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employer/placements`, {
        application_id: selectedApplicationForInterview.id,
        ...placementForm,
        offered_ctc: parseFloat(placementForm.offered_ctc) || 0
      });
      toast.success('Placement recorded!');
      setPlacementDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to record placement');
    }
  };

  const openInterviewDialog = (application) => {
    setSelectedApplicationForInterview(application);
    setInterviewForm({
      interview_date: '',
      interview_time: '',
      interview_type: 'In-Person',
      interview_location: profile?.address || '',
      interview_round: 1,
      interviewer_name: '',
      notes: ''
    });
    setInterviewDialogOpen(true);
  };

  const openPlacementDialog = (application) => {
    setSelectedApplicationForInterview(application);
    setPlacementForm({
      joining_date: '',
      offered_ctc: '',
      designation: application.job_title || '',
      department: '',
      offer_letter_url: ''
    });
    setPlacementDialogOpen(true);
  };

  // Filter candidates
  const filteredCandidates = candidates.filter(c => {
    if (candidateSearch && !c.name?.toLowerCase().includes(candidateSearch.toLowerCase())) return false;
    if (candidateFilters.course && !c.course?.toLowerCase().includes(candidateFilters.course.toLowerCase())) return false;
    if (candidateFilters.skills && !c.skills?.toLowerCase().includes(candidateFilters.skills.toLowerCase())) return false;
    if (candidateFilters.location && !c.location?.toLowerCase().includes(candidateFilters.location.toLowerCase())) return false;
    return true;
  });

  // Calculate stats
  const totalJobs = jobs.length;
  const activeJobs = jobs.filter(j => j.status === 'active' || !j.status).length;
  const totalApplications = analytics?.total_applications || jobs.reduce((sum, j) => sum + (j.applications_count || 0), 0);
  const shortlistedCount = enhancedAnalytics?.shortlisted_count || Math.floor(totalApplications * 0.3);
  const hiredCount = placements.length || enhancedAnalytics?.hired_count || 0;

  // InterviewsList component - fetches and displays scheduled interviews
  const InterviewsList = () => {
    const [interviews, setInterviews] = React.useState([]);
    const [loadingInterviews, setLoadingInterviews] = React.useState(true);

    React.useEffect(() => {
      const fetchInterviews = async () => {
        try {
          const res = await axios.get(`${API}/employer/interviews`);
          setInterviews(res.data || []);
        } catch {
          setInterviews([]);
        } finally {
          setLoadingInterviews(false);
        }
      };
      fetchInterviews();
    }, []);

    if (loadingInterviews) return <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto" /></div>;
    
    if (interviews.length === 0) return (
      <div className="text-center py-12">
        <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 mb-3">No interviews scheduled yet</p>
        <p className="text-sm text-slate-400">Shortlist candidates and schedule interviews from the Applications tab</p>
      </div>
    );

    return (
      <div className="space-y-4">
        {interviews.map(interview => (
          <div key={interview.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={`interview-${interview.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Video className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">{interview.candidate_name || 'Candidate'}</h4>
                  <p className="text-sm text-slate-500">
                    {interview.interview_date} at {interview.interview_time}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      interview.interview_type === 'online' ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'
                    }`}>
                      {interview.interview_type === 'online' ? 'Online' : 'In-Person'}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      interview.status === 'scheduled' ? 'bg-amber-50 text-amber-600' :
                      interview.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                    }`}>
                      {interview.status}
                    </span>
                  </div>
                  {interview.meeting_link && (
                    <a href={interview.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline mt-1 inline-block">
                      Join Meeting Link
                    </a>
                  )}
                  {interview.notes && <p className="text-xs text-slate-400 mt-1">{interview.notes}</p>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading Employer Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Modern Top Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm" data-testid="employer-header">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo & Company Info */}
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                {profile?.logo_url ? (
                  <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm" data-testid="header-company-logo">
                    <img src={profile.logo_url} alt={profile?.company_name || 'logo'} className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm" data-testid="header-company-logo">
                    {profile?.company_name ? profile.company_name.split(/\s+/).slice(0,2).map(w=>w[0]).join('').toUpperCase() : <Building2 className="w-7 h-7" />}
                  </div>
                )}
                <div className="hidden sm:block">
                  <h1 className="font-bold text-slate-800 text-lg leading-tight">{profile?.company_name || 'Company'}</h1>
                  <p className="text-xs text-slate-500">Employer Dashboard</p>
                </div>
              </Link>
            </div>

            {/* Center: Quick Stats on Desktop */}
            <div className="hidden lg:flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{totalJobs}</p>
                <p className="text-xs text-slate-500">Jobs</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{totalApplications}</p>
                <p className="text-xs text-slate-500">Applications</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{hiredCount}</p>
                <p className="text-xs text-slate-500">Hired</p>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {/* Post Job Button */}
              <Button 
                onClick={tryOpenJobForm}
                className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hidden sm:flex"
                data-testid="post-job-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Post Job
              </Button>

              {/* Notification Bell */}
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>
                
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-800">Notifications</h3>
                    </div>
                    {notifications.map(notif => (
                      <div key={notif.id} className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50">
                        <p className="text-sm text-slate-700">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{notif.time}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Profile Dropdown */}
              <div className="relative" ref={profileDropdownRef}>
                <button 
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className={`flex items-center gap-2 p-2 rounded-xl transition-all duration-200 ${
                    showProfileMenu ? 'bg-slate-100 ring-2 ring-blue-500/20' : 'hover:bg-slate-100'
                  }`}
                  data-testid="profile-menu"
                >
                  {profile?.logo_url ? (
                    <div className="w-9 h-9 rounded-full bg-white border border-slate-200 overflow-hidden flex items-center justify-center" data-testid="header-avatar-logo">
                      <img src={profile.logo_url} alt="logo" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center" data-testid="header-avatar-logo">
                      <span className="text-white font-medium text-sm">
                        {profile?.company_name?.charAt(0) || 'C'}
                      </span>
                    </div>
                  )}
                  <ChevronDown className={`w-4 h-4 text-slate-400 hidden sm:block transition-transform duration-200 ${showProfileMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {showProfileMenu && (
                  <div 
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                  >
                    <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-purple-50">
                      <p className="font-medium text-slate-800">{profile?.company_name}</p>
                      <p className="text-sm text-slate-500">{profile?.email || user?.email}</p>
                    </div>
                    <button 
                      onClick={() => { setActiveTab('profile'); setShowProfileMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                    >
                      <Building className="w-4 h-4" /> Company Profile
                    </button>
                    <button 
                      onClick={() => setShowProfileMenu(false)}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                    >
                      <Settings className="w-4 h-4" /> Settings
                    </button>
                    <hr className="my-2" />
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        
        {/* KPI Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-cards">
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Total Jobs Posted</p>
                <p className="text-3xl font-bold text-slate-800">{totalJobs}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">{activeJobs} active</span>
                </div>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                <Briefcase className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group"
            onClick={async () => {
              // Fetch ALL applications across all jobs
              try {
                const res = await axios.get(`${API}/employer/candidates`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});
                setSelectedJobApplications(res.data || []);
                setSelectedJob(null);
                setActiveTab('applications');
                if (tabsSectionRef.current) tabsSectionRef.current.scrollIntoView({ behavior: 'smooth' });
              } catch (e) { toast.error('Failed to load applications'); }
            }}
            data-testid="applications-received-card"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Applications Received</p>
                <p className="text-3xl font-bold text-slate-800">{totalApplications}</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-emerald-600 font-medium">Click to view all</span>
                </div>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Shortlisted</p>
                <p className="text-3xl font-bold text-amber-600">{shortlistedCount}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs text-slate-500">Ready for interview</span>
                </div>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl group-hover:bg-amber-200 transition-colors">
                <UserCheck className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all cursor-pointer text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-100 mb-1">Hired Candidates</p>
                <p className="text-3xl font-bold">{hiredCount}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Award className="w-4 h-4 text-emerald-200" />
                  <span className="text-xs text-emerald-100">Great progress!</span>
                </div>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <BadgeCheck className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm" data-testid="quick-actions">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Quick Actions
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={tryOpenJobForm}
              className="flex flex-col items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all group"
            >
              <div className="p-3 bg-blue-500 rounded-xl group-hover:scale-110 transition-transform">
                <Plus className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-slate-700">Post New Job</span>
            </button>
            
            <button 
              onClick={() => handleTabChange('candidates')}
              className="flex flex-col items-center gap-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-xl transition-all group"
            >
              <div className="p-3 bg-purple-500 rounded-xl group-hover:scale-110 transition-transform">
                <Search className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-slate-700">Search Candidates</span>
            </button>
            
            <button 
              onClick={() => handleTabChange('applications')}
              className="flex flex-col items-center gap-3 p-4 bg-amber-50 hover:bg-amber-100 rounded-xl transition-all group"
            >
              <div className="p-3 bg-amber-500 rounded-xl group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-slate-700">View Applications</span>
            </button>
            
            <button 
              onClick={() => handleTabChange('interviews')}
              className="flex flex-col items-center gap-3 p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-all group"
            >
              <div className="p-3 bg-emerald-500 rounded-xl group-hover:scale-110 transition-transform">
                <CalendarDays className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-slate-700">Schedule Interview</span>
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div ref={tabsSectionRef}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 mb-6 p-1 rounded-xl flex-wrap">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Overview
            </TabsTrigger>
            <TabsTrigger value="jobs" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              My Jobs
            </TabsTrigger>
            <TabsTrigger value="candidates" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Search Candidates
            </TabsTrigger>
            <TabsTrigger value="applications" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Applications
            </TabsTrigger>
            <TabsTrigger value="interviews" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Interviews
            </TabsTrigger>
            <TabsTrigger value="hired" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Hired
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Company Profile
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Recent Jobs */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-blue-500" />
                    Recent Job Postings
                  </h3>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab('jobs')} className="rounded-lg">
                    View All
                  </Button>
                </div>
                
                {jobs.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-3">No jobs posted yet</p>
                <Button onClick={tryOpenJobForm}>Post Your First Job</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobs.slice(0, 5).map(job => (
                      <div key={job.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Briefcase className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-800">{job.position_name}</h4>
                            <p className="text-sm text-slate-500 flex items-center gap-2">
                              <MapPin className="w-3 h-3" /> {job.location}
                              <span>•</span>
                              <span>{job.job_type}</span>
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-blue-600 font-medium">{job.applications_count || 0} apps</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            job.status === 'active' || !job.status ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {job.status || 'active'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notifications & Activity */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="notifications-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-red-500" />
                    Recent Activity
                  </h3>
                  <button onClick={fetchActivityFeed} className="text-blue-500 hover:text-blue-600 transition-colors">
                    <TrendingUp className={`w-4 h-4 ${activityLoading ? 'animate-pulse' : ''}`} />
                  </button>
                </div>
                <div className="space-y-3">
                  {activityLoading ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto" />
                      <p className="text-xs text-slate-500 mt-2">Loading...</p>
                    </div>
                  ) : activityFeed.length > 0 ? (
                    activityFeed.map(activity => {
                      const IconComponent = activityIconMap[activity.icon] || Bell;
                      return (
                        <div key={activity.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                          <div className={`p-2 rounded-lg ${
                            activity.color === 'blue' ? 'bg-blue-100' :
                            activity.color === 'purple' ? 'bg-purple-100' :
                            activity.color === 'emerald' ? 'bg-emerald-100' :
                            'bg-slate-100'
                          }`}>
                            <IconComponent className={`w-4 h-4 ${
                              activity.color === 'blue' ? 'text-blue-600' :
                              activity.color === 'purple' ? 'text-purple-600' :
                              activity.color === 'emerald' ? 'text-emerald-600' :
                              'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <p className="text-sm text-slate-700">{activity.message}</p>
                            <p className="text-xs text-slate-400 mt-1">{activity.time}</p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    notifications.map(notif => (
                      <div key={notif.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                        <div className={`p-2 rounded-lg ${
                          notif.type === 'application' ? 'bg-blue-100' :
                          notif.type === 'interview' ? 'bg-purple-100' : 'bg-emerald-100'
                        }`}>
                          {notif.type === 'application' ? <FileText className="w-4 h-4 text-blue-600" /> :
                           notif.type === 'interview' ? <Calendar className="w-4 h-4 text-purple-600" /> :
                           <BadgeCheck className="w-4 h-4 text-emerald-600" />}
                        </div>
                        <div>
                          <p className="text-sm text-slate-700">{notif.message}</p>
                          <p className="text-xs text-slate-400 mt-1">{notif.time}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* My Jobs Tab */}
          <TabsContent value="jobs">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-500" />
                  Job Listings ({jobs.length})
                </h3>
                <Button onClick={tryOpenJobForm} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Post New Job
                </Button>
              </div>

              {jobs.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">You haven't posted any jobs yet</p>
                  <Button onClick={tryOpenJobForm}>Post Your First Job</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobs.map(job => (
                    <div key={job.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                            <Briefcase className="w-7 h-7 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-800 text-lg">{job.position_name}</h4>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {job.location}</span>
                              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {job.job_type}</span>
                              <span className="flex items-center gap-1"><IndianRupee className="w-4 h-4" /> {job.min_ctc}-{job.max_ctc} LPA</span>
                              {job.application_deadline && (
                                <span className={`flex items-center gap-1 ${job.is_expired ? 'text-red-500' : 'text-rose-600'}`}>
                                  <Calendar className="w-4 h-4" /> Ends: {new Date(job.application_deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                            {job.skills && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {job.skills.split(',').slice(0, 4).map((skill, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{skill.trim()}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            job.is_expired ? 'bg-red-100 text-red-700' :
                            (job.status === 'active' || !job.status) ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {job.is_expired ? 'Expired' : (job.status || 'Active')}
                          </span>
                          <p className="text-sm text-slate-500 mt-2">{job.applications_count || 0} applications</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                        <Button size="sm" variant="outline" onClick={() => fetchApplications(job.id)} className="rounded-lg">
                          <Eye className="w-4 h-4 mr-1" /> View Applications
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEditJob(job)} className="rounded-lg">
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        {(job.status === 'active' || !job.status) && (
                          <Button size="sm" variant="outline" onClick={() => handleCloseJob(job.id)} className="rounded-lg text-amber-600 border-amber-200">
                            <XCircle className="w-4 h-4 mr-1" /> Close Job
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Search Candidates Tab */}
          <TabsContent value="candidates">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  Search Candidates
                </h3>
              </div>

              {/* Filters */}
              <div className="grid md:grid-cols-5 gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name..."
                      value={candidateSearch}
                      onChange={(e) => setCandidateSearch(e.target.value)}
                      className="pl-10 rounded-xl"
                    />
                  </div>
                </div>
                <div>
                  <Input
                    placeholder="Course"
                    value={candidateFilters.course}
                    onChange={(e) => setCandidateFilters({...candidateFilters, course: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Input
                    placeholder="Skills"
                    value={candidateFilters.skills}
                    onChange={(e) => setCandidateFilters({...candidateFilters, skills: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Input
                    placeholder="Location"
                    value={candidateFilters.location}
                    onChange={(e) => setCandidateFilters({...candidateFilters, location: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
              </div>

              {/* Candidate Cards */}
              {filteredCandidates.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No candidates found matching your criteria</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCandidates.map(candidate => (
                    <div key={candidate.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">{candidate.name?.charAt(0) || 'C'}</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">{candidate.name}</h4>
                          <p className="text-sm text-slate-500">{candidate.course || 'Student'}</p>
                        </div>
                        <span className={`ml-auto px-2 py-0.5 rounded-full text-xs ${
                          candidate.certified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {candidate.certified ? 'Certified' : 'In Training'}
                        </span>
                      </div>
                      
                      {candidate.skills && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {candidate.skills.split(',').slice(0, 3).map((skill, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{skill.trim()}</span>
                          ))}
                        </div>
                      )}
                      
                      {candidate.location && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mb-3">
                          <MapPin className="w-3 h-3" /> {candidate.location}
                        </p>
                      )}
                      
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1 rounded-lg">
                          <Eye className="w-4 h-4 mr-1" /> View Profile
                        </Button>
                        <Button size="sm" className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-lg">
                          <Send className="w-4 h-4 mr-1" /> Invite
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  {selectedJob ? `Applications for ${selectedJob.position_name}` : 'All Applications'}
                </h3>
                <div className="flex gap-2">
                  <Button size="sm" onClick={downloadCandidatesExcel} className="bg-green-600 hover:bg-green-700 rounded-lg" data-testid="download-candidates-excel">
                    <Download className="w-4 h-4 mr-1" /> Download Excel
                  </Button>
                  {selectedJob && (
                    <Button size="sm" variant="outline" onClick={() => { setSelectedJob(null); setSelectedJobApplications([]); }}>
                      Show All Jobs
                    </Button>
                  )}
                </div>
              </div>

              {!selectedJob && selectedJobApplications.length > 0 ? (
                <div className="space-y-4">
                  {selectedJobApplications.map(app => (
                    <div key={app.id} className="border border-slate-200 rounded-xl p-4" data-testid={`application-${app.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-lg">{(app.candidate_name || app.student_name || 'A').charAt(0)}</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{app.candidate_name || app.student_name}</h4>
                            <p className="text-sm text-slate-500">{app.candidate_email || app.student_email}</p>
                            {app.candidate_mobile && <p className="text-sm text-slate-500">{app.candidate_mobile}</p>}
                            <div className="flex flex-wrap gap-2 mt-1">
                              {app.candidate_dob && (
                                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                                  DOB: {app.candidate_dob}
                                </span>
                              )}
                              {app.candidate_qualification && (
                                <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <GraduationCap className="w-3 h-3" /> {app.candidate_qualification}
                                </span>
                              )}
                              {(app.candidate_city || app.candidate_district || app.candidate_state) && (
                                <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {[app.candidate_city, app.candidate_district, app.candidate_state].filter(Boolean).join(', ')}
                                  {app.candidate_pin_code && ` - ${app.candidate_pin_code}`}
                                </span>
                              )}
                              {app.job_title && (
                                <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">
                                  {app.job_title}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          app.status === 'shortlisted' ? 'bg-emerald-100 text-emerald-700' :
                          app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          app.status === 'hired' ? 'bg-blue-100 text-blue-700' :
                          app.status === 'interview_scheduled' ? 'bg-purple-100 text-purple-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {app.status === 'interview_scheduled' ? 'Interview Scheduled' : (app.status || 'Applied')}
                        </span>
                      </div>
                      {app.cover_letter && (
                        <p className="text-sm text-slate-600 mt-3 bg-slate-50 p-3 rounded-lg italic">"{app.cover_letter}"</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                        {app.status !== 'shortlisted' && app.status !== 'hired' && app.status !== 'rejected' && app.status !== 'interview_scheduled' && (
                          <Button size="sm" onClick={() => handleApplicationStatus(app.id, 'shortlisted')} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">
                            <CheckCircle className="w-4 h-4 mr-1" /> Shortlist
                          </Button>
                        )}
                        {(app.status === 'shortlisted' || app.status === 'interview_scheduled') && (
                          <>
                            <Button size="sm" onClick={() => openInterviewDialog(app)} className="bg-purple-600 hover:bg-purple-700 rounded-lg">
                              <Calendar className="w-4 h-4 mr-1" /> Schedule Interview
                            </Button>
                            <Button size="sm" onClick={() => openPlacementDialog(app)} className="bg-blue-600 hover:bg-blue-700 rounded-lg">
                              <BadgeCheck className="w-4 h-4 mr-1" /> Hire
                            </Button>
                          </>
                        )}
                        {app.status !== 'rejected' && app.status !== 'hired' && (
                          <Button size="sm" variant="outline" onClick={() => handleApplicationStatus(app.id, 'rejected')} className="text-red-600 border-red-200 rounded-lg">
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : !selectedJob ? (
                <div className="space-y-3">
                  <p className="text-slate-500 mb-4">Select a job to view its applications:</p>
                  {jobs.map(job => (
                    <div key={job.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 cursor-pointer" onClick={() => fetchApplications(job.id)}>
                      <div>
                        <h4 className="font-medium text-slate-800">{job.position_name}</h4>
                        <p className="text-sm text-slate-500">{job.location}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {job.applications_count || 0} applications
                        </span>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : selectedJobApplications.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No applications received yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedJobApplications.map(app => (
                    <div key={app.id} className="border border-slate-200 rounded-xl p-4" data-testid={`job-application-${app.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold">{(app.candidate_name || app.student_name || 'A').charAt(0)}</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{app.candidate_name || app.student_name}</h4>
                            <p className="text-sm text-slate-500">{app.candidate_email || app.student_email}</p>
                            {app.candidate_mobile && <p className="text-sm text-slate-500">{app.candidate_mobile}</p>}
                            <div className="flex flex-wrap gap-2 mt-1">
                              {app.candidate_qualification && (
                                <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <GraduationCap className="w-3 h-3" /> {app.candidate_qualification}
                                </span>
                              )}
                              {(app.candidate_district || app.candidate_state) && (
                                <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {[app.candidate_district, app.candidate_state].filter(Boolean).join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          app.status === 'shortlisted' ? 'bg-emerald-100 text-emerald-700' :
                          app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          app.status === 'hired' ? 'bg-blue-100 text-blue-700' :
                          app.status === 'interview_scheduled' ? 'bg-purple-100 text-purple-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {app.status === 'interview_scheduled' ? 'Interview Scheduled' : (app.status || 'Applied')}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                        {app.status !== 'shortlisted' && app.status !== 'hired' && app.status !== 'rejected' && (
                          <Button size="sm" onClick={() => handleApplicationStatus(app.id, 'shortlisted')} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">
                            <CheckCircle className="w-4 h-4 mr-1" /> Shortlist
                          </Button>
                        )}
                        {app.status === 'shortlisted' && (
                          <>
                            <Button size="sm" onClick={() => openInterviewDialog(app)} className="bg-purple-600 hover:bg-purple-700 rounded-lg">
                              <Calendar className="w-4 h-4 mr-1" /> Schedule Interview
                            </Button>
                            <Button size="sm" onClick={() => openPlacementDialog(app)} className="bg-blue-600 hover:bg-blue-700 rounded-lg">
                              <BadgeCheck className="w-4 h-4 mr-1" /> Hire
                            </Button>
                          </>
                        )}
                        {app.status !== 'rejected' && app.status !== 'hired' && (
                          <Button size="sm" variant="outline" onClick={() => handleApplicationStatus(app.id, 'rejected')} className="text-red-600 border-red-200 rounded-lg">
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-lg">
                          <Download className="w-4 h-4 mr-1" /> Resume
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Interviews Tab */}
          <TabsContent value="interviews">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-500" />
                Interview Management
              </h3>
              
              <InterviewsList />
            </div>
          </TabsContent>

          {/* Hired Tab */}
          <TabsContent value="hired">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-emerald-500" />
                Hired Candidates ({placements.length})
              </h3>
              
              {placements.length === 0 ? (
                <div className="text-center py-12">
                  <BadgeCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No candidates hired yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {placements.map(placement => (
                    <div key={placement.id} className="border border-emerald-200 bg-emerald-50 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                            <BadgeCheck className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{placement.student_name}</h4>
                            <p className="text-sm text-emerald-700">{placement.designation || placement.job_title}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <IndianRupee className="w-3 h-3" /> {placement.offered_ctc} LPA
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> Joined: {placement.joining_date || 'TBD'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-emerald-200 text-emerald-700 rounded-full text-xs font-medium">
                          Hired
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Company Profile Tab */}
          <TabsContent value="profile">
            <CompanyProfileEditor profile={profile} setProfile={setProfile} user={user} API={API} />
          </TabsContent>
        </Tabs>
        </div>
      </main>

      {/* Post Job Dialog */}
      <Dialog open={showJobForm} onOpenChange={setShowJobForm}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white text-lg">
                <Briefcase className="w-5 h-5" />
                {editingJob ? 'Edit Job Posting' : 'Post New Job'}
              </DialogTitle>
              <p className="text-blue-100 text-sm mt-0.5">Fill in the details to find the right candidates</p>
            </DialogHeader>
          </div>

          <form onSubmit={handlePostJob} className="px-6 pb-6 pt-2">
            {/* Section 1: Basic Information */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wide">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold">1</div>
                Basic Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <div className="md:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Job Title / Position Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={jobForm.position_name}
                    onChange={(e) => setJobForm({...jobForm, position_name: e.target.value})}
                    placeholder="e.g., Senior Software Developer, Data Analyst"
                    required
                    className={`rounded-[10px] h-11 bg-white shadow-sm border-slate-200 focus:border-blue-400 focus:ring-blue-400 placeholder:text-slate-300 ${!jobForm.position_name && isSubmitting ? 'border-red-400' : ''}`}
                    data-testid="job-title-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Location <span className="text-red-500">*</span></Label>
                  {profile?.branches && profile.branches.length > 0 ? (
                    <>
                      <select
                        value={jobForm.location}
                        onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
                        className={`w-full h-11 px-3 border rounded-[10px] bg-white shadow-sm text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none ${!jobForm.location && isSubmitting ? 'border-red-400' : 'border-slate-200'}`}
                        data-testid="job-location-select"
                      >
                        <option value="">Select Branch / Location</option>
                        {profile.branches.map((b, i) => (
                          <option key={b.id || i} value={[b.city, b.state].filter(Boolean).join(', ')}>
                            {b.name} - {b.city}{b.state ? `, ${b.state}` : ''}
                          </option>
                        ))}
                        <option value="__custom__">Other / Custom Location</option>
                      </select>
                      {jobForm.location === '__custom__' && (
                        <Input
                          value={jobForm.custom_location || ''}
                          onChange={(e) => setJobForm({...jobForm, custom_location: e.target.value, location: e.target.value})}
                          placeholder="Enter custom location"
                          className="rounded-[10px] mt-2 h-11 bg-white shadow-sm"
                        />
                      )}
                    </>
                  ) : (
                    <Input
                      value={jobForm.location}
                      onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
                      placeholder="e.g., Jaipur, Rajasthan"
                      required
                      className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 focus:border-blue-400 placeholder:text-slate-300"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Job Type</Label>
                  <select
                    value={jobForm.job_type}
                    onChange={(e) => setJobForm({...jobForm, job_type: e.target.value})}
                    className="w-full h-11 px-3 border border-slate-200 rounded-[10px] bg-white shadow-sm text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                    data-testid="job-type-select"
                  >
                    {JOB_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Min Experience (yrs)</Label>
                  <Input
                    type="number" min="0"
                    value={jobForm.experience_min}
                    onChange={(e) => setJobForm({...jobForm, experience_min: e.target.value})}
                    placeholder="0"
                    className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 placeholder:text-slate-300"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Max Experience (yrs)</Label>
                  <Input
                    type="number" min="0"
                    value={jobForm.experience_max}
                    onChange={(e) => setJobForm({...jobForm, experience_max: e.target.value})}
                    placeholder="5"
                    className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 placeholder:text-slate-300"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Salary & Openings */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wide">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-xs font-bold">2</div>
                Salary, Openings & Validity
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-emerald-50/30 p-4 rounded-xl border border-emerald-100">
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Min CTC (LPA)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500 text-sm font-medium">&#8377;</span>
                    <Input
                      type="number" min="0" step="0.5"
                      value={jobForm.min_ctc}
                      onChange={(e) => setJobForm({...jobForm, min_ctc: e.target.value})}
                      placeholder="3"
                      className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 pl-7 placeholder:text-slate-300"
                      data-testid="job-min-ctc"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Max CTC (LPA)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500 text-sm font-medium">&#8377;</span>
                    <Input
                      type="number" min="0" step="0.5"
                      value={jobForm.max_ctc}
                      onChange={(e) => setJobForm({...jobForm, max_ctc: e.target.value})}
                      placeholder="8"
                      className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 pl-7 placeholder:text-slate-300"
                      data-testid="job-max-ctc"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">No. of Openings</Label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setJobForm({...jobForm, num_requirements: Math.max(1, (parseInt(jobForm.num_requirements) || 1) - 1).toString()})}
                      className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 rounded-[10px] hover:bg-slate-50 shadow-sm text-lg font-bold text-slate-500" data-testid="openings-minus">
                      -
                    </button>
                    <Input
                      type="number" min="1"
                      value={jobForm.num_requirements}
                      onChange={(e) => setJobForm({...jobForm, num_requirements: e.target.value})}
                      className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 text-center font-semibold"
                      data-testid="job-openings"
                    />
                    <button type="button" onClick={() => setJobForm({...jobForm, num_requirements: ((parseInt(jobForm.num_requirements) || 0) + 1).toString()})}
                      className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 rounded-[10px] hover:bg-slate-50 shadow-sm text-lg font-bold text-blue-500" data-testid="openings-plus">
                      +
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-rose-500" />
                    Application End Date
                  </Label>
                  <Input
                    type="date"
                    value={jobForm.application_deadline}
                    onChange={(e) => setJobForm({...jobForm, application_deadline: e.target.value})}
                    min={new Date().toISOString().slice(0, 10)}
                    className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 focus:border-rose-400 focus:ring-rose-400"
                    data-testid="job-end-date"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Job auto-removes after this date</p>
                </div>
              </div>
            </div>

            {/* Section 3: Skills (Tags Input) */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wide">
                <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 text-xs font-bold">3</div>
                Required Skills
              </h4>
              <div className="bg-violet-50/30 p-4 rounded-xl border border-violet-100">
                <div className="flex flex-wrap gap-2 mb-2">
                  {(jobForm.skills || '').split(',').filter(s => s.trim()).map((skill, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-xs font-medium px-3 py-1.5 rounded-full">
                      {skill.trim()}
                      <button type="button" onClick={() => {
                        const skills = jobForm.skills.split(',').filter(s => s.trim());
                        skills.splice(idx, 1);
                        setJobForm({...jobForm, skills: skills.join(', ')});
                      }} className="text-violet-400 hover:text-violet-700 ml-0.5">
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <Input
                  placeholder="Type a skill and press Enter (e.g., Python, React, AutoCAD)"
                  className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 placeholder:text-slate-300"
                  data-testid="job-skills-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const val = e.target.value.trim().replace(/,/g, '');
                      if (val) {
                        const existing = jobForm.skills ? jobForm.skills + ', ' : '';
                        setJobForm({...jobForm, skills: existing + val});
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <p className="text-xs text-slate-400 mt-1.5">Press Enter or comma to add each skill</p>
              </div>
            </div>

            {/* Section 4: Description & Requirements */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wide">
                <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 text-xs font-bold">4</div>
                Description & Requirements
              </h4>
              <div className="space-y-4 bg-amber-50/20 p-4 rounded-xl border border-amber-100">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs font-semibold text-slate-600">Job Description</Label>
                    <span className={`text-xs ${(jobForm.description?.length || 0) > 900 ? 'text-red-500' : 'text-slate-400'}`}>
                      {jobForm.description?.length || 0}/1000
                    </span>
                  </div>
                  <div className="relative">
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      <button type="button" onClick={() => {
                        const sel = document.getElementById('desc-textarea');
                        const start = sel.selectionStart;
                        const end = sel.selectionEnd;
                        const text = jobForm.description || '';
                        if (start !== end) {
                          const newText = text.slice(0, start) + '**' + text.slice(start, end) + '**' + text.slice(end);
                          setJobForm({...jobForm, description: newText});
                        }
                      }} className="w-7 h-7 bg-white border border-slate-200 rounded-md flex items-center justify-center text-xs font-bold text-slate-500 hover:bg-slate-50" title="Bold">B</button>
                      <button type="button" onClick={() => {
                        setJobForm({...jobForm, description: (jobForm.description || '') + '\n- '});
                      }} className="w-7 h-7 bg-white border border-slate-200 rounded-md flex items-center justify-center text-xs text-slate-500 hover:bg-slate-50" title="Bullet">&#x2022;</button>
                    </div>
                    <textarea
                      id="desc-textarea"
                      value={jobForm.description}
                      onChange={(e) => setJobForm({...jobForm, description: e.target.value.slice(0, 1000)})}
                      placeholder="Describe the role, day-to-day responsibilities, team structure, growth opportunities..."
                      className="w-full min-h-[140px] px-3 py-3 border border-slate-200 rounded-[10px] bg-white shadow-sm resize-y text-sm placeholder:text-slate-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                      data-testid="job-description"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs font-semibold text-slate-600">Requirements & Qualifications</Label>
                    <span className={`text-xs ${(jobForm.requirements?.length || 0) > 900 ? 'text-red-500' : 'text-slate-400'}`}>
                      {jobForm.requirements?.length || 0}/1000
                    </span>
                  </div>
                  <div className="relative">
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      <button type="button" onClick={() => {
                        setJobForm({...jobForm, requirements: (jobForm.requirements || '') + '\n- '});
                      }} className="w-7 h-7 bg-white border border-slate-200 rounded-md flex items-center justify-center text-xs text-slate-500 hover:bg-slate-50" title="Bullet">&#x2022;</button>
                    </div>
                    <textarea
                      value={jobForm.requirements}
                      onChange={(e) => setJobForm({...jobForm, requirements: e.target.value.slice(0, 1000)})}
                      placeholder="List qualifications, certifications, experience needed...&#10;- B.Tech/BCA in relevant field&#10;- 2+ years experience&#10;- Good communication skills"
                      className="w-full min-h-[120px] px-3 py-3 border border-slate-200 rounded-[10px] bg-white shadow-sm resize-y text-sm placeholder:text-slate-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                      data-testid="job-requirements"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: SPOC Details (optional) */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wide">
                <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-slate-500 text-xs font-bold">5</div>
                Contact Person <span className="text-xs font-normal text-slate-400 normal-case">(Optional)</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">SPOC Name</Label>
                  <Input value={jobForm.spoc_name} onChange={(e) => setJobForm({...jobForm, spoc_name: e.target.value})} placeholder="Contact person" className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 placeholder:text-slate-300" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">SPOC Mobile</Label>
                  <Input value={jobForm.spoc_mobile} onChange={(e) => setJobForm({...jobForm, spoc_mobile: e.target.value})} placeholder="+91XXXXXXXXXX" className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 placeholder:text-slate-300" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">SPOC Email</Label>
                  <Input type="email" value={jobForm.spoc_email} onChange={(e) => setJobForm({...jobForm, spoc_email: e.target.value})} placeholder="email@company.com" className="rounded-[10px] h-11 bg-white shadow-sm border-slate-200 placeholder:text-slate-300" />
                </div>
              </div>
            </div>

            {/* Sticky Submit */}
            <div className="sticky bottom-0 bg-white pt-4 pb-2 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-3 -mx-6 px-6">
              <Button type="button" variant="outline" onClick={() => setShowJobForm(false)} className="rounded-[10px] h-11 px-6 border-slate-200 text-slate-600 hover:bg-slate-50 order-2 sm:order-1">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="rounded-[10px] h-11 px-8 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-200 order-1 sm:order-2" data-testid="submit-job-btn">
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Posting...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> {editingJob ? 'Update Job' : 'Post Job'}</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Interview Dialog */}
      <Dialog open={interviewDialogOpen} onOpenChange={setInterviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Schedule Interview
            </DialogTitle>
          </DialogHeader>
          {selectedApplicationForInterview && (
            <form onSubmit={handleScheduleInterview} className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-800">{selectedApplicationForInterview.student_name}</p>
                <p className="text-sm text-slate-500">{selectedApplicationForInterview.student_email}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={interviewForm.interview_date}
                    onChange={(e) => setInterviewForm({...interviewForm, interview_date: e.target.value})}
                    required
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input
                    type="time"
                    value={interviewForm.interview_time}
                    onChange={(e) => setInterviewForm({...interviewForm, interview_time: e.target.value})}
                    required
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label>Interview Type</Label>
                <select
                  value={interviewForm.interview_type}
                  onChange={(e) => setInterviewForm({...interviewForm, interview_type: e.target.value})}
                  className="w-full h-10 px-3 border rounded-xl bg-white"
                >
                  <option value="In-Person">In-Person</option>
                  <option value="Video Call">Video Call</option>
                  <option value="Phone">Phone</option>
                </select>
              </div>
              <div>
                <Label>Location / Link</Label>
                <Input
                  value={interviewForm.interview_location}
                  onChange={(e) => setInterviewForm({...interviewForm, interview_location: e.target.value})}
                  placeholder="Office address or meeting link"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Interviewer Name</Label>
                <Input
                  value={interviewForm.interviewer_name}
                  onChange={(e) => setInterviewForm({...interviewForm, interviewer_name: e.target.value})}
                  placeholder="Name of interviewer"
                  className="rounded-xl"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setInterviewDialogOpen(false)} className="rounded-xl">
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700 rounded-xl">
                  <Send className="w-4 h-4 mr-2" /> Schedule & Notify
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Hire / Placement Dialog */}
      <Dialog open={placementDialogOpen} onOpenChange={setPlacementDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-emerald-600" />
              Record Hiring
            </DialogTitle>
          </DialogHeader>
          {selectedApplicationForInterview && (
            <form onSubmit={handleRecordPlacement} className="space-y-4 mt-4">
              <div className="p-3 bg-emerald-50 rounded-xl">
                <p className="font-medium text-slate-800">{selectedApplicationForInterview.student_name}</p>
                <p className="text-sm text-emerald-700">Congratulations on the hire!</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Joining Date</Label>
                  <Input
                    type="date"
                    value={placementForm.joining_date}
                    onChange={(e) => setPlacementForm({...placementForm, joining_date: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>CTC Offered (LPA)</Label>
                  <Input
                    type="number"
                    value={placementForm.offered_ctc}
                    onChange={(e) => setPlacementForm({...placementForm, offered_ctc: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label>Designation</Label>
                <Input
                  value={placementForm.designation}
                  onChange={(e) => setPlacementForm({...placementForm, designation: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  value={placementForm.department}
                  onChange={(e) => setPlacementForm({...placementForm, department: e.target.value})}
                  placeholder="e.g., Engineering, Sales"
                  className="rounded-xl"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setPlacementDialogOpen(false)} className="rounded-xl">
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                  <BadgeCheck className="w-4 h-4 mr-2" /> Record Hiring
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployerDashboard;
